import { useState, useEffect, useMemo } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useFlowListSearchParams } from '../hooks/useFlowListSearchParams';
import type { FlowListItem } from '../types';
import { useFlowInstances } from '../../../hooks/useFlowInstances';
import { DateTime } from 'luxon';

import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { buildFilterParams } from '@Utils/utils';
import { useNavigate } from 'react-router-dom';
import { Store } from 'lucide-react';
import { useFlows } from '@Hooks/useFlows';
import { useQueryClient } from '@tanstack/react-query';
import { useFlowCategories } from '@Hooks/useFlowCategories';

const flowInstanceColumnConfigs: ColumnConfig<any>[] = [
  {
    id: 'flow.flowTitle',
    headerLabel: 'Flow Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'flow.category.name',
    headerLabel: 'Category',
    accessor: (row) => {
      return row.flowCategory ?? '-';
    },
    type: 'text',
  },
  {
    id: 'activeStage',
    headerLabel: 'Active Stage',
    accessor: (row) => {
      return row.activeStage.name ?? '-';
    },
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'status',
    headerLabel: 'Status',
    accessor: (row) => (row.status === 'completed' ? 'Completed' : 'In Progress'),
    type: 'text',
    enableSorting: false,
    cell: (info) => {
      const completed = info.row.original.status === 'completed';
      return (
        <Badge
          className={
            completed
              ? 'bg-green-100 text-green-800'
              : 'bg-amber-100 text-amber-800'
          }
        >
          {completed ? 'Completed' : 'In Progress'}
        </Badge>
      );
    },
  },
  {
    id: 'firstName',
    headerLabel: 'First Name',
    accessor: (row) => row.fishFirstName ?? '-',
    type: 'text',
  },
  {
    id: 'lastName',
    headerLabel: 'Last Name',
    accessor: (row) => row.fishLastName ?? '-',
    type: 'text',
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => row.fishEmail ?? '-',
    type: 'text',
  },
  {
    id: 'fish.phoneNumber',
    headerLabel: 'Phone Number',
    accessor: (row) => row.fishPhoneNumber ?? '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Started',
    accessor: (row) => row.startedAt ?? '-',
    type: 'text',
  },
  {
    id: 'lastActivity',
    headerLabel: 'Last Activity',
    accessor: (row) => row.lastActivityLabel ?? '-',
    type: 'text',
  },
];

export function FlowsList() {
  const [flowOptions, setFlowOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);

  const {
    flowFilter,
    setFlowFilter,
    activeStageFilter,
    setActiveStageFilter,
    categoryFilter,
    setCategoryFilter,
  } = useFlowListSearchParams();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: flowsRes } = useFlows();
  const flows = Array.isArray(flowsRes) ? flowsRes : [];

  const flowStages = useMemo(
    () => flows?.map((flow) => flow.flowStages).flat(),
    [flows],
  );

  const controller = useResourceTableController<any>({
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    columnsConfig: flowInstanceColumnConfigs,
    baseSearchParams: () => ({
      include:
        'flow.flowStages,flow.category,flowStageResults.flowStage,fish.fishProfiles',
    }),
    buildFilterParams: (filters) => buildFilterParams(filters),
    quickFilterFields: [
      { field: 'fish.fishProfiles.some.fName', label: 'First Name' },
      { field: 'fish.fishProfiles.some.lName', label: 'Last Name' },
      { field: 'fish.fishProfiles.some.email', label: 'Email' },
      { field: 'fish.phoneNumber', label: 'Phone Number' },
    ],
    initialPageSize: 10,
  });

  const { data: flowCategories } = useFlowCategories({
    searchParams: { include: 'flows' },
  });

  const { data: payload, status } = useFlowInstances(
    controller.searchParams,
    flowFilter,
    activeStageFilter,
    categoryFilter,
  );

  function formatDate(date) {
    return DateTime.fromISO(date).toFormat('MMM dd, yyyy hh:mm a');
  }

  function daysSince(dateString) {
    if (!dateString) return '-';
    const past = DateTime.fromISO(dateString).startOf('day');
    const today = DateTime.now().startOf('day');

    const days = today.diff(past, 'days').days;
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  }

  const instances = payload?.data?.map((instance: any) => {
    const mostRecent = instance.flowStageResults?.reduce((latest, stage) => {
      if (!latest) return stage;

      return new Date(stage.createdAt) > new Date(latest.createdAt)
        ? stage
        : latest;
    }, null);

    const days = daysSince(mostRecent?.createdAt);

    const fishProfile =
      instance?.fish?.fishProfiles?.find((p) => p.profileType === 'MEMBER') ??
      instance?.fish?.fishProfiles?.[0];
    const fishFirstName = fishProfile?.fName ?? '-';
    const fishLastName = fishProfile?.lName ?? '-';
    const fishEmail = fishProfile?.email ?? '-';
    const fishPhoneNumber = instance?.fish?.phoneNumber ?? '-';

    const flowCategory = instance?.flow?.category?.name;

    return {
      id: instance?.id,
      name: instance?.flow?.flowTitle,
      startedAt: formatDate(instance?.createdAt),
      lastActivityLabel: days,
      previousStage: {
        name: mostRecent?.flowStage?.title,
        status: 'previous',
      },
      activeStage: {
        name: instance.activeStage?.title,
        status: 'active',
      },
      status: instance.status,
      fishFirstName,
      fishLastName,
      fishEmail,
      fishPhoneNumber,
      flowCategory,
    } as FlowListItem;
  });

  const stageLabel =
    flowStages.find((stage) => stage.id === activeStageFilter)?.title ??
    'Select a Stage';
  const flowLabel =
    flows?.find((flow) => flow.id === flowFilter)?.flowTitle ?? 'Select a Flow';
  const categoryLabel =
    flowCategories?.data?.find((category) => category.id === categoryFilter)
      ?.name ?? 'Select a category';

  const rows: FlowListItem[] = Array.isArray(instances) ? instances : [];

  const getFlowsByCategory = (categoryId) => {
    const category = flowCategories?.data?.find(
      (category) => category.id === categoryId,
    );
    if (!category) return [];

    const flowIds = category.flows?.map((flow) => flow.id) ?? [];
    const flowIdSet = new Set(flowIds);

    const filteredFlows = flows?.filter((flow) => flowIdSet.has(flow.id));
    return filteredFlows ?? [];
  };

  const getStagesByCategory = (categoryId) => {
    const flows = getFlowsByCategory(categoryId);
    const stages = flows.map((flow) => flow.flowStages).flat();
    return stages;
  };

  const getStagesByFlowId = (flowId) => {
    const stages = flows?.find((f) => f.id === flowId)?.flowStages;
    return stages ?? [];
  };

  const updateFilters = () => {
    if (categoryFilter && flowFilter) {
      setFlowOptions(getFlowsByCategory(categoryFilter));
      setStageOptions(getStagesByFlowId(flowFilter));
    } else if (categoryFilter) {
      setFlowOptions(getFlowsByCategory(categoryFilter));
      setStageOptions(getStagesByCategory(categoryFilter));
    } else if (flowFilter) {
      setFlowOptions(flows);
      setStageOptions(getStagesByFlowId(flowFilter));
    } else {
      setFlowOptions(flows);
      setStageOptions(flowStages);
    }
  };

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['flowInstances'],
    });

    controller.setPageIndex(0);

    if (categoryFilter && flowFilter) {
      setFlowOptions(getFlowsByCategory(categoryFilter));
      setStageOptions(getStagesByFlowId(flowFilter));
    } else if (categoryFilter) {
      setFlowOptions(getFlowsByCategory(categoryFilter));
      setStageOptions(getStagesByCategory(categoryFilter));
    } else if (flowFilter) {
      setFlowOptions(flows);
      setStageOptions(getStagesByFlowId(flowFilter));
    } else {
      setFlowOptions(flows);
      setStageOptions(flowStages);
    }
  }, [flowFilter, activeStageFilter, categoryFilter]);

  useEffect(() => {
    if (!flows || !flowStages) return;

    if (categoryFilter && flowFilter) {
      setFlowOptions(getFlowsByCategory(categoryFilter));
      setStageOptions(getStagesByFlowId(flowFilter));
    } else if (categoryFilter) {
      setFlowOptions(getFlowsByCategory(categoryFilter));
      setStageOptions(getStagesByCategory(categoryFilter));
    } else if (flowFilter) {
      setFlowOptions(flows);
      setStageOptions(getStagesByFlowId(flowFilter));
    } else {
      setFlowOptions(flows);
      setStageOptions(flowStages);
    }
  }, [flowStages]);

  return (
    <div className="flex flex-col gap-6">
      {/* Filters + Sort */}
      <div className="flex items-center justify-between">
        {/* Sort */}
        <div className="flex gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-zinc-500">Flow Category:</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5">
                  {categoryLabel}
                  <ChevronDown className="ml-1 size-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setCategoryFilter(null);
                    updateFilters();
                  }}
                  className="cursor-pointer text-zinc-500"
                >
                  All Categories
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {flowCategories?.data?.map((fc) => (
                  <DropdownMenuItem
                    key={`category-${fc.id}`}
                    onClick={() => {
                      if (categoryFilter === fc.id) return;
                      setCategoryFilter(fc.id);
                      setFlowFilter(null);
                      setActiveStageFilter(null);
                    }}
                    className="cursor-pointer"
                  >
                    {fc.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-sm text-zinc-500">Flow:</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5">
                  {flowLabel}
                  <ChevronDown className="ml-1 size-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setFlowFilter(null);
                    updateFilters();
                  }}
                  className="cursor-pointer text-zinc-500"
                >
                  All Flows
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {flowOptions?.map((opt) => (
                  <DropdownMenuItem
                    key={`flow-${opt.id}`}
                    onClick={() => {
                      if (flowFilter === opt.id) return;
                      setFlowFilter(opt.id);
                      setActiveStageFilter(null);
                    }}
                    className="cursor-pointer"
                  >
                    {opt.flowTitle}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-sm text-zinc-500">Active Stage:</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5">
                  {stageLabel}
                  <ChevronDown className="ml-1 size-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setActiveStageFilter(null);
                    updateFilters();
                  }}
                  className="cursor-pointer text-zinc-500"
                >
                  All Stages
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {stageOptions?.map((opt) => (
                  <DropdownMenuItem
                    key={`stage-${opt.id}`}
                    onClick={() => {
                      setActiveStageFilter(opt.id);
                    }}
                    className="cursor-pointer"
                  >
                    {opt.title}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Card>
        <CardContent>
          <ResourceTableView<any>
            label="Flows"
            controller={controller}
            data={rows}
            meta={payload?.meta}
            status={status}
            summaryIcon={<Store className="h-4 w-4 text-zinc-500" />}
            summaryLabel="Total Flow Instances: "
            onRowClick={(row) => navigate(`/flows/list/${row.id}`)}
            renderFilterBar={false}
          />
        </CardContent>
      </Card>
    </div>
  );
}
